export interface JobTab {
  slug: string;
  label: string;
  /** Work-order status names this tab filters by. Empty = no status filter. */
  statusNames: string[];
  /**
   * MIR (More Information Requested) pending direction this tab filters by.
   * Used for tabs defined by a MIR cycle rather than a status — "More Info
   * Requested" has no dedicated work-order status; it is any job with an
   * outbound pending MIR (mirrors the "Awaiting reply" indicator on detail).
   */
  mirPendingDirection?: 'inbound' | 'outbound';
}

export const JOB_TABS: readonly JobTab[] = [
  { slug: 'all', label: 'All', statusNames: [] },
  { slug: 'new-orders', label: 'New Orders', statusNames: ['New'] },
  { slug: 'accepted', label: 'Accepted', statusNames: ['Ready for Planning'] },
  { slug: 'rejected', label: 'Rejected', statusNames: ['Cancelled'] },
  {
    slug: 'more-info-requested',
    label: 'More Info Requested',
    statusNames: [],
    mirPendingDirection: 'outbound',
  },
  {
    slug: 'ready-for-validation',
    label: 'Ready for Validation',
    statusNames: ['Validation Requested'],
  },
  { slug: 'validated', label: 'Validated', statusNames: ['Completed', 'Validated'] },
];

/**
 * The filter to apply for a given tab. A tab filters by EITHER status names OR
 * a MIR direction, never both — combining them in the same query would AND the
 * two conditions together and return nothing, since MIR-pending jobs do not
 * carry a "More Information Required" status.
 */
export interface JobTabFilter {
  work_order_status_name?: string[];
  mir_pending_direction?: 'inbound' | 'outbound';
}

/**
 * Resolve the query filter for a tab slug. Returns an empty object for the
 * "all" tab (or any unknown slug) so the caller applies no tab-driven filter.
 */
export function getTabFilter(slug: string): JobTabFilter {
  const tab = JOB_TABS.find((t) => t.slug === slug);
  if (!tab) return {};
  if (tab.mirPendingDirection) {
    return { mir_pending_direction: tab.mirPendingDirection };
  }
  if (tab.statusNames.length > 0) {
    return { work_order_status_name: tab.statusNames };
  }
  return {};
}
